<template>
	<NavSidebar
		v-bind="bindings"
		@nav="onNav"
		@action="onAction"
	/>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import { TelemetryLocation } from "@teaming/common-enums";
import NavSidebar from "./NavSidebar";
import { Themes as GroupThemes, Scales as GroupScales } from "./NavSidebarGroup";

export default {
	name: "NavSidebarHOC",
	components: {
		NavSidebar,
	},
	inject: [ "isLoggedIn", "userProfile", ],
	computed: {
		...mapGetters( "host-layout", [
			"myFavoriteTeams",
			"myFavoriteOneOnOnes",
			"toggleState",
		] ),
		primaryNavGroups() {
			const { name: routeName, path: routePath, } = this.$route;

			/**
			 * Menu group schema
			 * @prop {String} key
			 * @prop {String} text
			 * @prop {Array|null} icon
			 * @prop {Boolean} avatar
			 * @prop {Boolean} isSelected
			 * @prop {Object} emits
			 * @prop {String} emits.eventName - "nav" | "action"
			 * @prop {Object} emits.eventArgs - { name, [params, meta] } | { name, [ value ] }
			 * @prop {Array} [primaryItems]
			 * @prop {Array} [secondaryItems]
			 * @prop {String} [theme]
			 * @prop {String} [scale]
			 */

			const { myFavoriteTeams, myFavoriteOneOnOnes, } = this;
			const MAX_TOP_TEAMS = 5;

			const oneOnOnesGroup = {
				key: "nav:one-on-ones",
				text: this.translate( "oneOnOnesGroupTitle" ),
				tooltip: this.translate( "oneOnOnesGroupTitle" ),
				icon: [ "fac", "one-on-one", ],
				avatar: false,
				isSelected: routePath === "/one-on-ones",
				isSubSelected: routePath.startsWith( "/one-on-one" ),
				emits: {
					eventName: "nav",
					eventArgs: { name: "navigateToOneOnOnes", },
					create: {
						eventName: "action",
						eventArgs: { name: "host-dialogs/openNewOneOnOneDialog", value: {}, },
					},
				},
				semanticClass: "intercom--nav-sidebar-my-one-on-ones",
			};

			oneOnOnesGroup.primaryItems = myFavoriteOneOnOnes.slice( 0, MAX_TOP_TEAMS ).map( team => {
				const { id: teamId, } = team;
				const teamPath = `/one-on-one/${ teamId }`;
				return {
					key: `nav:1on1-${ team.id }`,
					text: team.name,
					isSelected: routePath.startsWith( teamPath ),
					emits: {
						eventName: "nav",
						// TODO: update this to nav to meetings once we enabled new 1:1 meetings in prod
						eventArgs: { name: "navigateToOneOnOneSharedStream", params: { teamId, }, },
					},
				};
			} );

			oneOnOnesGroup.secondaryItems = myFavoriteOneOnOnes.slice( MAX_TOP_TEAMS ).map( team => {
				const { id: teamId, } = team;
				const teamPath = `/one-on-one/${ teamId }`;
				return {
					key: `nav:team-${ team.id }`,
					text: team.name,
					isSelected: routePath.startsWith( teamPath ),
					emits: {
						eventName: "nav",
						// TODO: update this to nav to meetings once we enabled new 1:1 meetings in prod
						eventArgs: { name: "navigateToOneOnOneSharedStream", params: { teamId, }, },
					},
				};
			} );

			const teamsGroup = {
				key: "nav:teams",
				text: this.translate( "teamsGroupTitle" ),
				tooltip: this.translate( "teamsGroupTitle" ),
				icon: [ "fas", "users", ],
				avatar: false,
				isSelected: routePath === "/teams",
				isSubSelected: routePath.startsWith( "/team" ),
				emits: {
					eventName: "nav",
					eventArgs: { name: "navigateToTeams", },
					// TODO: uncomment later
					// create: {
					// 	eventName: "action",
					// 	eventArgs: { name: "createTeam", },
					// },
				},
				semanticClass: "intercom--nav-sidebar-my-teams",
			};

			teamsGroup.primaryItems = myFavoriteTeams.slice( 0, MAX_TOP_TEAMS ).map( team => {
				const { id: teamId, } = team;
				const teamPath = `/team/${ teamId }`;
				return {
					key: `nav:team-${ team.id }`,
					text: team.name,
					isSelected: routePath.startsWith( teamPath ),
					emits: {
						eventName: "nav",
						eventArgs: { name: "navigateToTeam", params: { teamId, }, },
					},
				};
			} );

			teamsGroup.secondaryItems = myFavoriteTeams.slice( MAX_TOP_TEAMS ).map( team => {
				const { id: teamId, } = team;
				const teamPath = `/team/${ teamId }`;
				return {
					key: `nav:1on1-${ team.id }`,
					text: team.name,
					isSelected: routePath.startsWith( teamPath ),
					emits: {
						eventName: "nav",
						eventArgs: { name: "navigateToTeam", params: { teamId, }, },
					},
				};
			} );

			const profileGroup = {
				key: "nav:profile",
				text: this.translate( "profileGroupTitle" ),
				tooltip: this.translate( "profileGroupTitle" ),
				icon: null,
				avatar: true,
				isSelected: routePath === "/me/profile",
				emits: {
					eventName: "nav",
					eventArgs: { name: "navigateToProfile", },
				},
				semanticClass: "intercom--nav-sidebar-my-profile",
			};

			const goalsGroup = {
				key: "nav:goals",
				text: this.translate( "goalsGroupTitle" ),
				tooltip: this.translate( "goalsGroupTitle" ),
				icon: [ "fas", "bullseye-arrow", ],
				avatar: false,
				isSelected: routeName.startsWith( "MY_GOALS" ),
				emits: {
					eventName: "nav",
					eventArgs: { name: "navigateToMyGokrs", },
				},
				semanticClass: "intercom--nav-sidebar-goals",
			};

			const commitmentsGroup = {
				key: "nav:commitments",
				text: this.translate( "commitmentsGroupTitle" ),
				tooltip: this.translate( "commitmentsGroupTitle" ),
				icon: [ "fas", "check-double", ],
				avatar: false,
				isSelected: routeName === "Action Item Listing",
				emits: {
					eventName: "nav",
					eventArgs: { name: "navigateToMyCommitments", },
				},
				semanticClass: "intercom--nav-sidebar-my-action-items",
			};

			const allPrimaryNavGroups = [
				oneOnOnesGroup,
				teamsGroup,
				profileGroup,
				goalsGroup,
				commitmentsGroup,
			].filter( itemGroup => !!itemGroup )
				.map( itemGroup => {
					// set some defaults
					return {
						primaryItems: [],
						secondaryItems: [],
						theme: GroupThemes.PRIMARY,
						scale: GroupScales.NORMAL,
						semanticClass: "",
						emits: {},
						isSubSelected: false,
						...itemGroup,
					};
				} );

			return allPrimaryNavGroups;
		},
		secondaryNavGroups() {
			const { path: routePath, } = this.$route;
			const { showPaymentOption, } = this.userProfile;

			const subscribeGroup = {
				key: "nav:subscribe",
				text: this.translate( "subscribeGroupTitle" ),
				tooltip: this.translate( "subscribeGroupTitle" ),
				icon: [ "fas", "dollar-sign", ],
				avatar: false,
				isSelected: false,
				emits: {
					eventName: "nav",
					eventArgs: {
						name: "navigateToStripePayment",
						params: {
							personId: this.userProfile.personId,
							email: this.userProfile.email,
							location: TelemetryLocation.PRIMARY_NAV,
						},
					},
				},
				theme: GroupThemes.PAYMENT,
				semanticClass: "intercom--nav-sidebar-trial-mode",
			};

			let coachingDemoGroup;
			if ( showPaymentOption ) {
				coachingDemoGroup = {
					key: "nav:demo",
					text: this.translate( "demoGroupTitle" ),
					tooltip: this.translate( "demoGroupTitle" ),
					icon: [ "fas", "comments-question-check", ],
					avatar: false,
					isSelected: false,
					emits: {
						eventName: "nav",
						eventArgs: { name: "navigateToDemo", },
					},
					theme: GroupThemes.SPECIAL,
					semanticClass: "intercom--nav-sidebar-schedule-session",
				};
			}

			const settingsGroup = {
				key: "nav:settings",
				text: this.translate( "settingsGroupTitle" ),
				tooltip: this.translate( "settingsGroupTitle" ),
				icon: [ "fas", "sliders-h", ],
				avatar: false,
				isSelected: routePath.startsWith( "/me/settings" ),
				emits: {
					eventName: "nav",
					eventArgs: { name: "navigateToUserSettings", },
				},
				semanticClass: "intercom--nav-sidebar-settings-and-billing",
			};

			const signOutGroup = {
				key: "nav:sign-out",
				text: this.translate( "signOutGroupTitle" ),
				tooltip: this.translate( "signOutGroupTitle" ),
				icon: [ "fas", "power-off", ],
				avatar: false,
				isSelected: false,
				emits: {
					eventName: "nav",
					eventArgs: { name: "logout", },
				},
			};

			return [
				this.$foundation().config.showStripeIntegration && showPaymentOption ? subscribeGroup : null,
				coachingDemoGroup,
				settingsGroup,
				signOutGroup,
			].filter( itemGroup => !!itemGroup )
				.map( itemGroup => {
					// set some defaults
					return {
						primaryItems: [],
						secondaryItems: [],
						theme: GroupThemes.PRIMARY,
						scale: GroupScales.SMALL,
						semanticClass: "",
						emits: {},
						isSubSelected: false,
						...itemGroup,
					};
				} );
		},
		bindings() {
			const {
				userProfile: user,
				primaryNavGroups,
				secondaryNavGroups,
				toggleState,
			} = this;

			return {
				user,
				primaryNavGroups,
				secondaryNavGroups,
				toggleState,
			};
		},
	},
	methods: {
		...mapMutations( "host-layout", [ "closeNavSidebar", ] ),
		translate( key, args = {} ) {
			return this.$t( `teaming-web.layouts.NavSidebarHOC.${ key }`, args );
		},
		onNav( eventArgs ) {
			this.closeNavSidebar();

			if ( !eventArgs ) {
				return;
			}

			const navHandler = this.$foundation().navigation[ eventArgs.name ];
			if ( !navHandler ) {
				// eslint-disable-next-line no-console
				console.error( `failed to resolve route ${ eventArgs.name }` );
				return;
			}

			navHandler( eventArgs.params || {} );
		},
		async onAction( eventArgs ) {
			this.closeNavSidebar();

			if ( !eventArgs ) {
				return;
			}

			await this.$nextTick();

			if ( eventArgs.name ) {
				this.$store.dispatch( eventArgs.name, eventArgs.value );
			}
		},
	},
};
</script>
